import { BufferGeometry, Matrix4, Mesh, Object3D } from 'three';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree, MeshBVH } from 'three-mesh-bvh';

import { traverse } from '@spazious/dollhouse-utils';

import { Outline2D } from '../math/index.js';
import { isMesh } from '../utils/index.js';
declare module 'three' {
  export interface BufferGeometry {
    boundsTree: MeshBVH | null;
    computeBoundsTree: typeof computeBoundsTree;
    disposeBoundsTree: typeof disposeBoundsTree;
    baseOutline: Outline2D | null;
  }

  export interface Raycaster {
    firstHitOnly?: boolean;
  }

  export interface Mesh {
    raycast: typeof acceleratedRaycast;
  }

  export interface Object3D {
    intersects: typeof acceleratedIntersect;
  }
}

// BVH Shim
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
BufferGeometry.prototype.boundsTree = null;
BufferGeometry.prototype.baseOutline = null;
Mesh.prototype.raycast = acceleratedRaycast;
Object3D.prototype.intersects = acceleratedIntersect;

const invMat = new Matrix4();

const ensureBoundsTree = (geometry: BufferGeometry) => {
  const { boundsTree } = geometry;

  if (!boundsTree) {
    geometry.computeBoundsTree();
  }

  if (!boundsTree) {
    throw new Error('BoundsTree not found');
  }

  return boundsTree;
};

function acceleratedIntersect(
  this: Object3D,
  object: Object3D,
  strategy: 'sphere' | 'box' | 'geometry' = 'sphere'
) {
  let hitTest = false;

  traverse(this, (source) => {
    if (isMesh(source)) {
      const sourceBoundTree = ensureBoundsTree(source.geometry);
      const transformMatrix = invMat.copy(source.matrixWorld).invert();
      return traverse(object, (target) => {
        if (isMesh(target)) {
          transformMatrix.multiply(object.matrixWorld);

          switch (strategy) {
            case 'sphere':
              if (!target.geometry.boundingSphere) {
                target.geometry.computeBoundingSphere();
              }
              hitTest = sourceBoundTree.intersectsSphere(target.geometry.boundingSphere!);
              break;
            case 'box':
              if (!target.geometry.boundingBox) {
                target.geometry.computeBoundingBox();
              }
              hitTest = sourceBoundTree.intersectsBox(
                target.geometry.boundingBox!,
                transformMatrix
              );
              break;
            case 'geometry':
              hitTest = sourceBoundTree.intersectsGeometry(target.geometry, transformMatrix);
              break;
          }

          if (hitTest) {
            return false;
          }
        }
      });
    }
  });

  return hitTest;
}
